import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@i18n/apiError';
import { useResendMagicLink } from '@hooks/auth/useMagicLink';
import {
  CustomerPortalLogoSizeInput,
  getCustomerPortalLogoSizeStyles,
} from '@lib/customerPortalLogoSize';

interface CustomStyles {
  primaryBackgroundColor: string | null;
  secondaryBackgroundColor: string | null;
  primaryTextColor: string | null;
  secondaryTextColor: string | null;
  buttonBackgroundColor: string | null;
  buttonTextColor: string | null;
  logo: string | null;
  logoSize: CustomerPortalLogoSizeInput;
}

interface EmailSentConfirmationProps {
  email?: string;
  magicLinkEmail?: string;
  customStyles?: CustomStyles;
  domain?: string;
  isLoading?: boolean;
}

const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`skeleton-pulse rounded ${className}`} />
);

const LoadingSkeleton: React.FC = () => (
  <div className="flex justify-center items-center min-h-screen bg-white px-4 sm:px-8">
    <div className="w-full max-w-[500px] bg-white p-6 sm:p-8 rounded-[8px] shadow-[0px_4px_20px_rgba(0,0,0,0.08)]">
      <div className="flex justify-center mb-6">
        <Skeleton className="w-[180px] h-[60px]" />
      </div>
      <div className="text-center mb-6">
        <Skeleton className="w-[60%] h-[40px] mx-auto mb-3" />
        <Skeleton className="w-[90%] h-[24px] mx-auto" />
      </div>
      <div className="mt-6">
        <Skeleton className="w-[85%] h-[20px] mx-auto mb-6" />
        <Skeleton className="w-[50%] h-[20px] mx-auto mb-2" />
        <Skeleton className="w-full h-[48px]" />
      </div>
    </div>
  </div>
);

const EmailSentConfirmation: React.FC<EmailSentConfirmationProps> = ({
  email = '',
  magicLinkEmail = '',
  customStyles,
  domain,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const resendMagicLinkMutation = useResendMagicLink();

  const s = {
    primaryBg: customStyles?.primaryBackgroundColor ?? '#FFFFFF',
    secondaryBg: customStyles?.secondaryBackgroundColor ?? '#F8F9FA',
    primaryTxt: customStyles?.primaryTextColor ?? '#000000',
    secondaryTxt: customStyles?.secondaryTextColor ?? '#6B7280',
    btnBg: customStyles?.buttonBackgroundColor ?? '#2563EB',
    btnTxt: customStyles?.buttonTextColor ?? '#FFFFFF',
    logo: customStyles?.logo ?? null,
    logoSize: customStyles?.logoSize,
  };

  const displayEmail = magicLinkEmail || email;

  const handleResend = () => {
    if (!domain || !displayEmail) return;
    setResendError(null);
    setResendSuccess(false);

    resendMagicLinkMutation.mutate(
      { email: displayEmail, domain },
      {
        onSuccess: () => {
          setResendSuccess(true);
          setTimeout(() => setResendSuccess(false), 3000);
        },
        onError: (err) => setResendError(apiErrorMessage(err)),
      },
    );
  };

  const resending = resendMagicLinkMutation.isPending;

  if (isLoading) return <LoadingSkeleton />;

  return (
    <div
      className="flex justify-center items-center min-h-screen px-4 sm:px-8 transition-all duration-300"
      style={{ backgroundColor: s.primaryBg }}
    >
      <div
        className="w-full max-w-[500px] p-6 sm:p-8 rounded-[8px] transition-all duration-300"
        style={{
          backgroundColor: s.secondaryBg,
          boxShadow: '13px 10px 39px -3px rgba(0,0,0,0.1)',
        }}
      >
        <div className="flex justify-center mb-6">
          <img
            src={s.logo ?? '/assets/images/logos/lasso-new-logo-white-transparentbg.png'}
            alt={t('common.logoAlt')}
            style={{
              ...getCustomerPortalLogoSizeStyles(s.logoSize),
              objectFit: 'contain',
              maxWidth: '100%',
              filter: s.logo ? 'none' : 'invert(1)',
            }}
          />
        </div>

        <h1
          className="primary-font-medium text-center text-[20px] sm:text-[24px] font-bold mb-2"
          style={{ color: s.primaryTxt }}
        >
          {t('auth.emailSent.title')}
        </h1>
        <p
          className="primary-font-medium text-center text-[14px] sm:text-[16px]"
          style={{ color: s.secondaryTxt }}
        >
          <Trans
            i18nKey="auth.emailSent.sentTo"
            values={{ email: displayEmail }}
            components={{
              highlight: <span className="font-medium" style={{ color: s.btnBg }} />,
            }}
          />
        </p>

        <p
          className="primary-font-regular text-center text-[13px] sm:text-[14px] mt-8"
          style={{ color: s.secondaryTxt }}
        >
          {t('auth.emailSent.instructions')}
        </p>

        {resendSuccess && (
          <p className="text-center text-[13px] text-green-600 mt-4">
            {t('auth.emailSent.resendSuccess')}
          </p>
        )}
        {resendError && <p className="text-center text-[13px] text-red-600 mt-4">{resendError}</p>}

        <p className="text-center text-[13px] mt-6" style={{ color: s.secondaryTxt }}>
          {t('auth.emailSent.didntReceive')}
        </p>
        <button
          onClick={handleResend}
          disabled={resending}
          className="w-full mt-2 py-3 rounded text-[14px] sm:text-[16px] font-medium capitalize transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: s.btnBg, color: s.btnTxt }}
        >
          {resending ? t('common.sending') : t('auth.emailSent.resend')}
        </button>
      </div>
    </div>
  );
};

export default EmailSentConfirmation;
